export type ShownImage = 'front' | 'hover'

/** Minimum snap distance so short/imprecise swipes on small cards still require intent. */
const MIN_SNAP_THRESHOLD_PX = 50
/** Snap distance scales with card width so the gesture feels consistent across grid columns. */
const SNAP_THRESHOLD_RATIO = 0.25
/** Movement below this, in any direction, is treated as a tap rather than the start of a swipe. */
const TAP_SLOP_PX = 8

/** Duration of the release animation that snaps the drag back to front/hover. */
export const SNAP_TRANSITION_MS = 200

export const getSnapThreshold = (width: number): number =>
  Math.max(MIN_SNAP_THRESHOLD_PX, SNAP_THRESHOLD_RATIO * width)

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

interface WrapperOffsets {
  front: number
  hover: number
}

// The shown image tracks the finger in EITHER direction; the other image is
// parked just off the edge the finger pulls FROM, so it slides in to fill the
// gap. At rest (drag 0) the other image sits fully off-screen right and is
// clipped by the container's overflow:hidden.
export const getWrapperOffsets = ({
  shown,
  drag,
  width,
}: {
  shown: ShownImage
  drag: number
  width: number
}): WrapperOffsets => {
  if (width <= 0) {
    return { front: 0, hover: 0 }
  }

  const shownOffset = clamp(drag, -width, width)
  const otherOffset = shownOffset + (shownOffset <= 0 ? width : -width)

  return shown === 'front'
    ? { front: shownOffset, hover: otherOffset }
    : { front: otherOffset, hover: shownOffset }
}

// Past the threshold in EITHER direction flips to the other image (wrap-around).
export const resolveSnap = ({
  shown,
  drag,
  width,
  threshold,
}: {
  shown: ShownImage
  drag: number
  width: number
  threshold: number
}): ShownImage => {
  if (width <= 0) {
    return shown
  }

  const shownOffset = clamp(drag, -width, width)

  if (Math.abs(shownOffset) >= threshold) {
    return shown === 'front' ? 'hover' : 'front'
  }

  return shown
}

/**
 * Axis-lock: only claim the gesture as a horizontal swipe once movement clears
 * the tap slop AND the horizontal component dominates, so vertical page scroll
 * stays native and a tap never gets misread as a swipe.
 */
export const shouldStartHorizontalSwipe = (
  dx: number,
  dy: number,
  slop: number = TAP_SLOP_PX,
): boolean => {
  const absDx = Math.abs(dx)
  const absDy = Math.abs(dy)

  if (absDx < slop && absDy < slop) {
    return false
  }

  return absDx > absDy
}
